import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'antd/lib/select';

import { isPresent } from '@common/typescript/objects/Nullable';

import { DeliveryType, DeliveryTypeName } from '@app/objects/Pet';
import { Value } from '@app/components/UI/Filters/FilterHook';
import { Price } from '@app/objects/Price';

const deliveryTypeKey = 'delivery-type';

interface DeliveryTypeSelectProps {
	value: Value | undefined;

	prices?: Array<Price> | false;

	onChange: (value: Value) => void;
	onDeselect?: (fieldName: string, type: string, id?: number) => void;

	disabled?: boolean;
	withFieldName?: boolean;
	multiple?: boolean;

	className?: string;
	selectClassName?: (value: string | number | undefined, length: number) => string;
}

interface DeliveryTypeValues {
	key: string;
	value: string;
}

export const DeliveryTypeSelect: React.FC<DeliveryTypeSelectProps> = (props: DeliveryTypeSelectProps) => {
	const { t } = useTranslation();
	const [query, setQuery] = React.useState<string>('');

	const translator = React.useCallback((key: string) => t(`filters.${key}`), []);

	const deliveryTypeValues = React.useMemo<Array<DeliveryTypeValues>>(() => {
		const keys = Object.keys(DeliveryType).sort((a, b) => (a >= b ? 1 : -1));

		if (props.prices === false) {
			return keys.map((key: string) => {
				const deliveryType = DeliveryType[key];
				const deliveryTypeName = DeliveryTypeName[key];

				const name = t(`enums.${deliveryTypeKey}.${deliveryTypeName}`);

				return { key: deliveryType, value: name };
			});
		}

		if (!Array.isArray(props.prices) || props.prices.length === 0) {
			return [];
		}

		const prices: Array<Price> = props.prices;

		return keys
			.map((key: string) => {
				const deliveryType = DeliveryType[key];
				const deliveryTypeName = DeliveryTypeName[key];

				const name = t(`enums.${deliveryTypeKey}.${deliveryTypeName}`);

				const p = prices.find((i) => i.deliveryType === deliveryType)?.value;
				if (!isPresent(p)) return null;

				const priceLabel = p === 0 ? 'Free' : `$${p.toFixed(2)}`;

				return {
					key: deliveryType,
					value: `${name} (${priceLabel})`,
				};
			})
			.filter(isPresent);
	}, [props.prices, t]);

	return (
		<div className={props.className}>
			{props.withFieldName && <span className="field-name">{translator(deliveryTypeKey)}</span>}
			<span className="ant-select-selection__placeholder">{translator(deliveryTypeKey)}</span>
			<Select
				className={props.selectClassName ? props.selectClassName(props.value, query.length) : ''}
				showSearch
				showArrow
				showAction={['focus', 'click']}
				optionFilterProp="children"
				placeholder={translator(deliveryTypeKey)}
				mode={props.multiple ? 'multiple' : undefined}
				value={props.value === -1 ? undefined : props.value}
				onChange={props.onChange}
				onSearch={(query: string) => setQuery(query)}
				onDeselect={(value: number) => props.onDeselect && props.onDeselect('deliveryType', 'id', value)}
				disabled={props.disabled}
				onSelect={() => setQuery('')}
				onBlur={() => setQuery('')}
			>
				{
					(deliveryTypeValues as Array<DeliveryTypeValues>).map(({ key, value }) => (
						<Select.Option value={key} key={key}>
							{value}
						</Select.Option>
					))
				}
			</Select>
		</div>
	);
};
